import {
  ChangeDetectorRef,
  Component,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';

import { FormsModule } from '@angular/forms';

import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';

import { NavigationEnd, Router, RouterLink } from '@angular/router';

import { filter, merge, Subject, takeUntil } from 'rxjs';

import { FuseNavigationService } from '../components/fuse-navigation/navigation/navigation.service';

import { GetFavouriteService } from '@_services/get-favourite.service';
import { BehaviorService } from '@_services/Behavior.service';

import { ToastrService } from 'ngx-toastr';

import { TierPermissionPipe } from '@_pipes/tier-permission.pipe';

@Component({
  selector: 'navigation',

  standalone: true,

  imports: [FormsModule, MatIconModule, MatSlideToggleModule, MatTooltipModule, RouterLink],

  providers: [TierPermissionPipe],

  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <div class="flex h-full min-h-0 flex-col">
      <div class="min-h-0 flex-1 overflow-x-hidden overflow-y-auto py-3">
        <div class="flex max-w-[90%] flex-col gap-1 [&>a]:rounded-r-[20px] [&>a]:ps-24">
          @for (item of visibleMenus; track item.ID) {
            <a
              class="group rounded-xl flex cursor-pointer items-center px-3 py-3 text-sm font-medium text-white transition-all duration-200 hover:bg-white/10 hover:text-primary-50"
              [class.justify-center]="isCollapsed"
              [class.gap-3]="!isCollapsed"
              [class.bg-primary-50]="isMenuActive(item)"
              [class.text-[#38bdf8]]="isMenuActive(item)"
              [routerLink]="currentUser?.OPENINNEWTAB == 0 ? [item.URL] : null"
              (click)="handleNavigation($event, item)"
              [matTooltip]="isCollapsed ? item.TITLE : ''"
              matTooltipPosition="right"
            >
              <!-- ICON -->
              <div class="flex size-22 min-w-22 items-center justify-center">
                <img
                  [src]="getMenuIcon(item.TITLE)"
                  [alt]="item.TITLE"
                  class="size-22 object-contain"
                />
              </div>
              @if (!isCollapsed) {
                <span class="flex-1 truncate">
                  {{ item.TITLE }}
                </span>
              }

              @if (EDITFavourite && !isCollapsed) {
                <button
                  type="button"
                  class="flex items-center justify-center"
                  (click)="toggleFavourite($event, item)"
                >
                  <mat-icon
                    class="!h-[20px] !min-h-[20px] !w-[20px] !min-w-[20px] cursor-pointer !text-[20px] leading-none text-yellow-400"
                    [fontIcon]="item.STAR === 'star' ? 'star' : 'star_border'"
                  ></mat-icon>
                </button>
              }
            </a>
          }
        </div>
      </div>

      @if (!isCollapsed) {
        <div class="shrink-0 border-t border-white/10 p-3">
          <label class="flex items-center gap-3 text-white">
            <mat-slide-toggle
              color="primary"
              [(ngModel)]="EDITFavourite"
              (change)="toggleEditFavourite()"
            ></mat-slide-toggle>

            <span>Edit Favourites</span>
          </label>
        </div>
      }
    </div>
  `,
})
export class Navigation implements OnInit, OnDestroy {
  @Input() isCollapsed = false;
  currentUser: any = JSON.parse(localStorage.getItem('currentUser') || '{}');

  page: any[] = [];

  visibleMenus: any[] = [];

  favouriteMenuList: any[] = [];

  appPermissions: any = JSON.parse(localStorage.getItem('appPermissions') || '{}');

  EDITFavourite = false;

  target: any = {};

  showAuth = true;

  showSearching = true;

  showDairy = true;

  showTrustData = true;

  showSafeCustodyData = true;

  showshowTaskPipeData = true;

  showInboxData = true;

  private _unsubscribeAll = new Subject<void>();

  private router = inject(Router);

  constructor(
    private _changeDetectorRef: ChangeDetectorRef,
    private _fuseNavigationService: FuseNavigationService,
    private getFavouriteService: GetFavouriteService,
    private toastr: ToastrService,
    private behaviorService: BehaviorService,
    private tierpermission: TierPermissionPipe,
  ) {
    this.loadPermissions();

    this.loadMenu();

    this.behaviorService.toolBaarSetting$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((result: any) => {
        this.currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
        if (result?.OPENINNEWTAB !== undefined && result?.OPENINNEWTAB !== null) {
          this.currentUser.OPENINNEWTAB = result.OPENINNEWTAB;
        }
        this.target = result?.OPENINNEWTABWINDOWS || {};

        this.refreshPageTargets();
      });

    this.behaviorService.GetPreferences$.pipe(takeUntil(this._unsubscribeAll)).subscribe(
      (result: any) => {
        if (!result) {
          return;
        }
        this.currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
        if (result.OPENINNEWTAB !== undefined && result.OPENINNEWTAB !== null) {
          this.currentUser.OPENINNEWTAB = result.OPENINNEWTAB;
        }
      },
    );
  }

  ngOnInit(): void {
    merge(
      this._fuseNavigationService.onNavigationItemAdded,
      this._fuseNavigationService.onNavigationItemUpdated,
      this._fuseNavigationService.onNavigationItemRemoved,
    )
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(() => {
        this._changeDetectorRef.markForCheck();
      });

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this._unsubscribeAll),
      )
      .subscribe(() => {
        this._changeDetectorRef.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next();

    this._unsubscribeAll.complete();
  }

  loadMenu(): void {
    const guid = JSON.parse(localStorage.getItem('currentUser') || '{}');

    if (!guid?.UserGuid && !guid?.USERGUID) {
      return;
    }

    const teTabID: any = {
      matters: 'MATTERS',

      contact: 'CONTACTS',

      invoice: 'INVOICES',

      time_entries: 'TIMEENTRIES',

      diary: 'DIARY',

      'spend-money': 'SPENDMONEY',

      authorities: 'AUTHORITIES',

      searching: 'SEARCHING',

      'full-Safe-Custody': 'SAFECUSTODY',

      'Safe-Custody': 'SAFECUSTODY',

      'trust-money': 'TRUSTMONEY',

      'trust-end-month': 'TRUSTENDOFMONTH',
    };

    const favouriteList: any[] = [
      {
        ID: 'matters',
        TITLE: 'Matters',
        URL: 'matters',
        STAR: '',
      },

      {
        ID: 'contact',
        TITLE: 'Contacts',
        URL: 'contact',
        STAR: '',
      },

      {
        ID: 'time_entries',
        TITLE: 'Time entries',
        URL: 'time-entries',
        STAR: '',
      },

      {
        ID: 'diary',
        TITLE: 'Diary',
        URL: 'diary',
        STAR: '',
      },

      {
        ID: 'invoice',
        TITLE: 'Invoices',
        URL: 'invoice',
        STAR: '',
      },

      {
        ID: 'spend-money',
        TITLE: 'Spend money',
        URL: 'spend-money',
        STAR: '',
      },

      {
        ID: 'authorities',
        TITLE: 'Authorities',
        URL: 'authorities',
        STAR: '',
      },

      {
        ID: 'searching',
        TITLE: 'Searching',
        URL: 'searching',
        STAR: '',
      },

      {
        ID: 'full-Safe-Custody',
        TITLE: 'Safe Custody',
        URL: 'full-Safe-Custody',
        STAR: '',
      },

      {
        ID: 'task',
        TITLE: 'Task',
        URL: 'task',
        STAR: '',
      },

      {
        ID: 'receive-money',
        TITLE: 'Receive money',
        URL: 'receive-money',
        STAR: '',
      },

      {
        ID: 'conflict-check',
        TITLE: 'Conflict Check',
        URL: 'conflict-check',
        STAR: '',
      },

      {
        ID: 'inbox',
        TITLE: 'Inbox',
        URL: 'email',
        STAR: '',
      },
    ];

    if (guid.PRODUCTTYPE !== 'Barrister') {
      favouriteList.push(
        {
          ID: 'trust-money',
          TITLE: 'Trust Money',
          URL: 'trust-money',
          STAR: '',
        },
        {
          ID: 'trust-end-month',
          TITLE: 'Trust End Of Month',
          URL: 'trust-end-month',
          STAR: '',
        },
      );
    }

    const stored = JSON.parse(localStorage.getItem('usersFavouritelist') || '{}');

    if (
      stored?.DATA?.RECORDS &&
      Array.isArray(stored.DATA.RECORDS) &&
      stored.DATA.RECORDS.length > 0
    ) {
      this.prepareMenuData(stored.DATA.RECORDS, teTabID, guid);

      return;
    }

    const payload = {
      Action: 'GetData',

      Filters: {
        USERGUID: guid.UserGuid,
      },
    };

    this.getFavouriteService
      .GetFavourite(payload)
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe({
        next: (response: any) => {
          if (response?.CODE === 200 && response?.STATUS === 'success') {
            localStorage.setItem('usersFavouritelist', JSON.stringify(response));

            if (
              !response?.DATA?.RECORDS ||
              response?.DATA?.RECORDS === '' ||
              response?.DATA?.RECORDS?.length === 0
            ) {
              this.getFavouriteService
                .setFavourite({
                  RECORDS: favouriteList,
                })
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe({
                  next: () => {
                    const saveData = {
                      DATA: {
                        RECORDS: favouriteList,
                      },
                    };

                    localStorage.setItem('usersFavouritelist', JSON.stringify(saveData));

                    this.prepareMenuData(favouriteList, teTabID, guid);
                  },

                  error: (err) => {
                    this.toastr.error(err);
                  },
                });
            } else {
              this.prepareMenuData(response.DATA.RECORDS, teTabID, guid);
            }
          }
        },

        error: (error) => {
          this.toastr.error(error);
        },
      });
  }

  prepareMenuData(records: any[], teTabID: any, guid: any): void {
    this.page = records
      .filter((x: any) => x && x.ID && x.TITLE)
      .map((item: any) => ({
        ...item,

        openInNewTab:
          typeof this.target?.[teTabID[item.ID]] === 'undefined'
            ? 0
            : this.target?.[teTabID[item.ID]],

        type: 'item',

        icon: 'Frame 36.svg',
      }));

    if (this.appPermissions?.[27] && this.appPermissions?.[27]?.['Snap Shot'] == 0) {
      this.page = this.page.filter((e: any) => e.ID !== 'dashboard');
    }

    if (guid?.EmailSystem === 'SMTP') {
      this.page = this.page.filter((e: any) => e.ID !== 'inbox');
    }

    this.updateVisibleMenus();
  }

  updateVisibleMenus(): void {
    let menus = this.page.filter((item: any) => item && item.TITLE && this.showMenuItem(item));

    if (!this.EDITFavourite) {
      menus = menus.filter((x: any) => x.STAR === 'star');
    }

    this.visibleMenus = menus;
  }

  toggleEditFavourite(): void {
    this.updateVisibleMenus();
  }

  toggleFavourite(event: Event, item: any): void {
    event.preventDefault();

    event.stopPropagation();

    item.STAR = item.STAR === 'star' ? '' : 'star';

    const payload = {
      RECORDS: this.page,
    };

    this.getFavouriteService
      .setFavourite(payload)
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe({
        next: () => {
          const stored = JSON.parse(localStorage.getItem('usersFavouritelist') || '{}');

          if (stored?.DATA?.RECORDS) {
            const index = stored.DATA.RECORDS.findIndex((x: any) => x.ID === item.ID);

            if (index !== -1) {
              stored.DATA.RECORDS[index].STAR = item.STAR;

              localStorage.setItem('usersFavouritelist', JSON.stringify(stored));
            }
          }

          this.updateVisibleMenus();
        },

        error: (err) => {
          this.toastr.error(err);
        },
      });
  }

  handleNavigation(event: Event, item: any): void {
    if (this.currentUser?.OPENINNEWTAB == 1) {
      event.preventDefault();

      const target = item.openInNewTab ? '_blank' : '_self';

      this.navigateTab(item.URL, target);
    }
  }

  navigateTab(url: string, target: string): void {
    const windowName = url?.replace(/-/g, '') || '_blank';

    const ref = window.open('/#/' + url, windowName);

    ref?.focus();
  }

  showMenuItem(item: any): boolean {
    switch (item.TITLE) {
      case 'Authorities':
        return this.showAuth;

      case 'Searching':
        return this.showSearching;

      case 'Diary':
        return this.showDairy;

      case 'Trust Money':

      case 'Trust End Of Month':
        return this.showTrustData;

      case 'Safe Custody':
        return this.showSafeCustodyData;

      case 'Tasks':
        return this.showshowTaskPipeData;

      case 'Inbox':
        return this.showInboxData;

      default:
        return true;
    }
  }

  loadPermissions(): void {
    this.showAuth = this.tierpermission.transform('Authorities')?.ALLOWED == 1;

    this.showSearching = this.tierpermission.transform('Searching')?.ALLOWED == 1;

    this.showDairy = this.tierpermission.transform('Diary')?.ALLOWED == 1;

    this.showTrustData = this.tierpermission.transform('Trust Accounts')?.ALLOWED == 1;

    this.showSafeCustodyData = this.tierpermission.transform('Safe Custody')?.ALLOWED == 1;

    this.showshowTaskPipeData = this.tierpermission.transform('Tasks')?.ALLOWED == 1;

    this.showInboxData = this.tierpermission.transform('Inbox')?.ALLOWED == 1;
  }

  refreshPageTargets(): void {
    this.page = this.page.map((item: any) => ({
      ...item,
      openInNewTab: this.getOpenInNewTab(item.ID),
    }));
  }

  isMenuActive(item: any): boolean {
    const segment = this.router.url.split('?')[0].replace(/^\//, '').split('/')[0];
    const tabId = window.name?.split('_')?.[1];
    const fromContact =
      sessionStorage.getItem('CLIENT_FROM_CONTACT') === 'true' ||
      (!!tabId && sessionStorage.getItem(`CONTACT_CLIENT_${tabId}`) === 'CONTACT');

    if (item.URL === 'matters') {
      return (
        segment === 'matters' ||
        segment === 'matter-details' ||
        (segment === 'client-details' && !fromContact)
      );
    }

    if (item.URL === 'contact') {
      return segment === 'contact' || (segment === 'client-details' && fromContact);
    }

    return segment === item.URL;
  }

  getOpenInNewTab(id: string): number {
    if (!id) {
      return 0;
    }

    const map: any = {
      matters: 'MATTERS',

      contact: 'CONTACTS',

      invoice: 'INVOICES',

      time_entries: 'TIMEENTRIES',

      diary: 'DIARY',

      'spend-money': 'SPENDMONEY',

      authorities: 'AUTHORITIES',

      searching: 'SEARCHING',

      'full-Safe-Custody': 'SAFECUSTODY',

      'Safe-Custody': 'SAFECUSTODY',

      'trust-money': 'TRUSTMONEY',

      'trust-end-month': 'TRUSTENDOFMONTH',
    };

    return this.target?.[map[id]] ?? 0;
  }

  getMenuIcon(title: string): string {
    const icons: any = {
      Matters: 'assets/icons/web_app/Frame 36.svg',

      Contacts: 'assets/icons/web_app/Contact Base Icon 2.svg',

      'Time entries': 'assets/icons/web_app/Timer Base Icon2.svg',

      Diary: 'assets/icons/web_app/diary base icon.svg',

      Invoices: 'assets/icons/web_app/Invoice Icon Base Style.svg',

      'Spend money': 'assets/icons/web_app/Spend Money Base Icon2.svg',

      'Receive money': 'assets/icons/web_app/Receive Money Icon.svg',

      Authorities: 'assets/icons/web_app/Authorities Base Icon.svg',

      Tasks: 'assets/icons/web_app/Tasks Icon Base Style dark mode.svg',

      Searching: 'assets/icons/web_app/searching base darkmode.svg',

      'Safe Custody': 'assets/icons/web_app/Safe Custody Base Style.svg',

      'Trust Money': 'assets/icons/web_app/Trust Icon Base Style.svg',

      'Trust End Of Month': 'assets/icons/web_app/Trust End of Month.svg',

      'Conflict Check': 'assets/icons/web_app/Conflict_Check_Icon.svg',

      Inbox: 'assets/icons/web_app/base icon.svg',

      Dashboard: 'assets/icons/web_app/dashboard2.svg',

      'Bank Feeds': 'assets/icons/web_app/Frame 118.svg',
    };

    return icons[title] || 'assets/icons/web_app/dashboard2.svg';
  }

  @HostListener('window:resize')
  onResize(): void {
    this._changeDetectorRef.detectChanges();
  }
}
